import { Routes } from '@angular/router';
import { authGuard, guestGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { ShellComponent } from './layout/shell/shell.component';
import { LoginComponent } from './features/auth/login.component';
import { LandingComponent } from './features/landing/landing.component';

export const routes: Routes = [
  {
    // Public front door — shown to everyone, signed in or not. Bouncing an
    // existing session straight to /dashboard made the site open on a desk page
    // instead of the landing page.
    path: '',
    pathMatch: 'full',
    component: LandingComponent,
  },
  {
    path: 'login',
    canActivate: [guestGuard],
    component: LoginComponent,
  },
  {
    path: '',
    canActivate: [authGuard],
    component: ShellComponent,
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        canActivate: [roleGuard],
        data: { module: 'dashboard' },
      },
      {
        path: 'assistant',
        loadComponent: () =>
          import('./features/assistant/assistant.component').then((m) => m.AssistantComponent),
        canActivate: [roleGuard],
        data: { module: 'assistant' },
      },
      {
        path: 'complaints',
        loadComponent: () =>
          import('./features/complaints/complaint-list.component').then((m) => m.ComplaintListComponent),
        canActivate: [roleGuard],
        data: { module: 'complaints' },
      },
      {
        // Must come before 'complaints/:id' — otherwise the router greedily matches
        // 'search' as an :id param and routes here to the detail page instead.
        path: 'complaints/search',
        loadComponent: () =>
          import('./features/complaints/complaint-search.component').then((m) => m.ComplaintSearchComponent),
        canActivate: [roleGuard],
        data: { module: 'complaints' },
      },
      {
        path: 'complaints/:id',
        loadComponent: () =>
          import('./features/complaints/complaint-detail.component').then((m) => m.ComplaintDetailComponent),
        canActivate: [roleGuard],
        data: { module: 'complaints' },
      },
      {
        path: 'workflow',
        loadComponent: () =>
          import('./features/workflow/workflow.component').then((m) => m.WorkflowComponent),
        canActivate: [roleGuard],
        data: { module: 'workflow' },
      },
      {
        path: 'cases',
        loadComponent: () => import('./features/cases/cases.component').then((m) => m.CasesComponent),
        canActivate: [roleGuard],
        data: { module: 'cases' },
      },
      {
        path: 'scheduling',
        loadComponent: () =>
          import('./features/scheduling/scheduling.component').then((m) => m.SchedulingComponent),
        canActivate: [roleGuard],
        data: { module: 'scheduling' },
      },
      { path: 'hearings', redirectTo: 'hearing-management', pathMatch: 'full' },
      {
        path: 'hearing-management',
        loadComponent: () =>
          import('./features/hearing-management/hearing-management.component').then(
            (m) => m.HearingManagementComponent,
          ),
        canActivate: [roleGuard],
        data: { module: 'hearingManagement' },
      },
      {
        path: 'roster',
        loadComponent: () => import('./features/roster/roster.component').then((m) => m.RosterComponent),
        canActivate: [roleGuard],
        data: { module: 'roster' },
      },
      {
        path: 'resolution',
        loadComponent: () =>
          import('./features/resolution/resolution.component').then((m) => m.ResolutionComponent),
        canActivate: [roleGuard],
        data: { module: 'resolution' },
      },
      { path: 'resolutions', redirectTo: 'resolution', pathMatch: 'full' },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/notifications/notifications.component').then((m) => m.NotificationsComponent),
        canActivate: [roleGuard],
        data: { module: 'notifications' },
      },
      {
        path: 'reports',
        loadComponent: () => import('./features/reports/reports.component').then((m) => m.ReportsComponent),
        canActivate: [roleGuard],
        data: { module: 'reports' },
      },
      { path: 'analytics', redirectTo: 'reports', pathMatch: 'full' },
      {
        path: 'document-ocr',
        loadComponent: () =>
          import('./features/document-ocr/document-ocr-shell.component').then((m) => m.DocumentOcrShellComponent),
        canActivate: [roleGuard],
        data: { module: 'documentOcr' },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'capture' },
          {
            path: 'capture',
            loadComponent: () =>
              import('./features/document-ocr/ocr-capture.component').then((m) => m.OcrCaptureComponent),
          },
          {
            path: 'documents',
            loadComponent: () =>
              import('./features/document-ocr/ocr-documents.component').then((m) => m.OcrDocumentsComponent),
          },
          {
            path: 'review/:id',
            loadComponent: () =>
              import('./features/document-ocr/ocr-review.component').then((m) => m.OcrReviewComponent),
          },
        ],
      },
      {
        path: 'administration',
        loadComponent: () =>
          import('./features/administration/administration-dashboard.component').then(
            (m) => m.AdministrationDashboardComponent,
          ),
        canActivate: [roleGuard],
        data: { module: 'settings' },
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/settings/settings.component').then((m) => m.SettingsComponent),
        canActivate: [roleGuard],
        data: { module: 'settings' },
      },
      {
        path: 'location-access',
        loadComponent: () =>
          import('./features/location-access/location-access.component').then((m) => m.LocationAccessComponent),
        canActivate: [roleGuard],
        data: { module: 'settings' },
      },
      {
        path: 'roles',
        loadComponent: () => import('./features/roles/roles.component').then((m) => m.RolesComponent),
        canActivate: [roleGuard],
        data: { module: 'roles' },
      },
      {
        path: 'resources',
        loadComponent: () =>
          import('./features/resources/resources.component').then((m) => m.ResourcesComponent),
        canActivate: [roleGuard],
        data: { module: 'roles' },
      },
      { path: 'admin', redirectTo: 'settings', pathMatch: 'full' },
      {
        path: 'profile',
        loadComponent: () => import('./features/profile/profile.component').then((m) => m.ProfileComponent),
        canActivate: [roleGuard],
        data: { module: 'profile' },
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
